// app/src/lib/cashflow/scenarios.js — session-only "what if" overlays (US-3.6).
//
// Hard invariants:
//   - Never persists. The adjustment stack lives in module memory only and is
//     wiped on `clear()` or when the module is reloaded.
//   - Maximum 3 simultaneous adjustments per session.
//   - Forecast consumers receive a shallow copy; they cannot mutate stored state.

const MAX_ADJUSTMENTS = 3;

/**
 * @typedef {{ id:string, type:'delayAr', arId:string, deltaDays:number, label?:string }
 *         | { id:string, type:'shiftInvoice', invoiceId:string, deltaDays:number, label?:string }
 *         | { id:string, type:'adHoc', dateIso:string, amountCents:number, label?:string }} Adjustment
 */

/** @type {Adjustment[]} */
let stack = [];

export function list() { return stack.map((a) => ({ ...a })); }

export function add(adjustment) {
  validate(adjustment);
  if (stack.length >= MAX_ADJUSTMENTS) {
    const err = new Error('Max 3 adjustments per session');
    err.code = 'TOO_MANY_ADJUSTMENTS';
    throw err;
  }
  const row = { id: crypto.randomUUID(), ...adjustment };
  stack = [...stack, row];
  return row;
}

export function remove(id) {
  stack = stack.filter((a) => a.id !== id);
}

export function clear() { stack = []; }

function validate(a) {
  if (!a || typeof a !== 'object') throw new Error('adjustment must be an object');
  if (!['delayAr', 'shiftInvoice', 'adHoc'].includes(a.type)) throw new Error('unknown adjustment type');
  if (a.type === 'delayAr') {
    if (!a.arId || !Number.isInteger(a.deltaDays)) throw new Error('delayAr: arId + integer deltaDays required');
  } else if (a.type === 'shiftInvoice') {
    if (!a.invoiceId || !Number.isInteger(a.deltaDays)) throw new Error('shiftInvoice: invoiceId + integer deltaDays required');
  } else if (a.type === 'adHoc') {
    if (typeof a.dateIso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(a.dateIso)) throw new Error('adHoc: dateIso ISO YYYY-MM-DD required');
    if (!Number.isFinite(a.amountCents)) throw new Error('adHoc: amountCents required');
  }
}
